import {DataTable, DataTableProps} from "../DataTable/DataTable";
import {ObjectMeterCheckAssoc} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback} from "react";
import {ActionIcon, Group} from "@mantine/core";
import {HasAccess} from "../../auth/HasAccess/HasAccess"
import {IconTrash} from "@tabler/icons-react";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {usePagination} from "../DataTable/usePagination";
import {useObjectMeterCheckAssocDataTableFilter} from "./useObjectMeterCheckAssocDataTableFilter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {EntityIcon} from "../../icon/EntityIcon/EntityIcon";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

const EMPTY_ARRAY: ObjectMeterCheckAssoc[] = []

export type ObjectMeterCheckAssocDataTableProps =
  EntityDataTableProps<ObjectMeterCheckAssoc>
  & {
  onDelete?: (record: ObjectMeterCheckAssoc) => void
}

export const ObjectMeterCheckAssocDataTable = (props: ObjectMeterCheckAssocDataTableProps) => {
  const {
    records: objectMeterCheckAssocs = EMPTY_ARRAY,
    recordsPerPageOptions,
    onDelete,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useObjectMeterCheckAssocDataTableFilter({
    records: objectMeterCheckAssocs
  })

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'objectName', direction: 'asc'},
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const onDeleteClick = useCallback((record: ObjectMeterCheckAssoc) => {
    if (onDelete) {
      onDelete(record)
    }
  }, [onDelete])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          ...getColumnFilterProps(filterConfig, "object"),
          title: t("table.entity.objectMeterCheckassoc.column.object", "Object"),
          accessor: "objectName",
          width: 100,
          sortable: true,
          render: ({objectId, objectName, objectType}) => {
            return (
              <Group spacing="xs">
                {objectType &&
                  <EntityIcon
                    entityType={objectType}
                    size="0.9em"
                  />
                }
                <EntityAnchor
                  value={{
                    objectId: objectId,
                    objectName: objectName,
                    objectType: objectType
                  }}
                />
              </Group>
            )
          },
        },
        {
          title: '',
          accessor: "actions",
          width: 100,
          textAlignment: 'right',
          render: (meterFlowVolume) => (
            <HasAccess permissions={["edit"]}>
              <Group spacing={4} position="right" noWrap>
                <ActionIcon color="red" onClick={() => onDeleteClick(meterFlowVolume)}>
                  <IconTrash size={16}/>
                </ActionIcon>
              </Group>
            </HasAccess>
          )
        }
      ]}
    />
  );
};
